import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTimes,
    faUserPlus,
    faSpinner,
    faBuilding,
    faGlobe,
    faTag,
    faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { crmService } from '../../services/crmService';
import { COUNTRY_OPTIONS } from '../../utils/constants';
import { PARTNER_TYPE_OPTIONS } from '../../constants/crmVocabularies';
import { getErrorMessage } from '../../utils/errorMessages';

/**
 * Inline CRM Creator
 *
 * Mirrors the InlineProductCreator pattern for CRM partners. Lets a user
 * create a *minimal* CRM profile from inside any deal flow (Trade Recap,
 * Delivery, Offer, Fuel Enquiry, etc.) without leaving the form they were
 * filling in.
 *
 * Compliance contract:
 *   - Hits POST /api/crm/quick-create-profile/ which only requires
 *     IsAuthenticated (NOT HasCRMAccess), so trading users can use it.
 *   - The new profile is forced to approval_status='pending' and
 *     created_via='deal_flow' for audit.
 *   - The picker that opened this modal is responsible for surfacing the
 *     just-created profile locally (with a "Pending Approval" badge) until
 *     a CRM approver completes the full record. Downstream finalize
 *     boundaries (contract sign, invoice issue, delivery confirm) should
 *     still require approved status.
 *
 * Props:
 *   - isOpen (bool): show / hide the modal.
 *   - onClose (fn): cancel handler — caller's form state must be untouched.
 *   - onPartnerCreated (fn(profile)): success handler. Receives the new
 *     profile blob; caller is responsible for preselecting it on the
 *     picker that triggered the flow.
 *   - initialLegalEntityName (string): seeds the search term the user
 *     just typed in the picker.
 *   - initialPartnerType (string): defaults the partner_type select based
 *     on the picker's role context (e.g. "Buyer (Debtor)" for a buyer
 *     picker, "Ship Owners" for a vessel-owner picker).
 *   - title (string): override modal title for picker-specific wording.
 */

// Vocabulary comes from the shared constants module — this file used to
// carry its own copy in a different order.

const InlineCrmCreator = ({
    isOpen,
    onClose,
    onPartnerCreated,
    initialLegalEntityName = '',
    initialPartnerType = 'Seller (Creditor)',
    title = 'Create CRM Partner',
}) => {
    const [legalEntityName, setLegalEntityName] = useState(initialLegalEntityName || '');
    const [tradingShortName, setTradingShortName] = useState('');
    const [country, setCountry] = useState('');
    const [partnerType, setPartnerType] = useState(initialPartnerType || 'Seller (Creditor)');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    // Reset form when modal opens with a fresh search term / role context.
    useEffect(() => {
        if (isOpen) {
            setLegalEntityName(initialLegalEntityName || '');
            setTradingShortName('');
            setCountry('');
            setPartnerType(initialPartnerType || 'Seller (Creditor)');
            setError('');
            setIsSubmitting(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, initialLegalEntityName, initialPartnerType]);

    if (!isOpen) return null;

    const trimmedName = legalEntityName.trim();
    const canSubmit = trimmedName.length > 0 && !isSubmitting;

    const handleSubmit = async (e) => {
        e.preventDefault();
        // The pop-up is drawn inside screens that are forms themselves (the
        // delivery screen). Its submit must stop here: it used to carry on to
        // the delivery's own Save, which then saved (or, on a new delivery,
        // created) the delivery before the new partner was even picked.
        e.stopPropagation();
        setError('');
        if (!trimmedName) {
            setError('Legal Entity Name is required.');
            return;
        }
        setIsSubmitting(true);
        try {
            const countryLabel = country
                ? (COUNTRY_OPTIONS.find((c) => c.value === country)?.label || country)
                : '';

            const newProfile = await crmService.quickCreateProfile({
                legal_entity_name: trimmedName,
                trading_short_name: tradingShortName.trim(),
                partner_type: partnerType,
                country: countryLabel,
            });
            onPartnerCreated?.(newProfile);
        } catch (err) {
            console.error('InlineCrmCreator: failed to create partner', err);
            // Backend already-exists path: surface a helpful message and
            // let the user pick the existing profile manually.
            if (err?.isDuplicate && err?.existingProfile) {
                setError(
                    `A CRM profile with this legal entity name already exists `
                    + `("${err.existingProfile.legal_entity_name}", `
                    + `${err.existingProfile.approval_status}). `
                    + `Cancel and search for it in the picker.`
                );
            } else {
                setError(getErrorMessage(err, 'Failed to create partner. Please try again.'));
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    // Drawn on the page body, as on HMS, so its form is never nested inside
    // the screen's form in the page. That alone does not stop React passing
    // the pop-up's submit up to the screen: handleSubmit's stopPropagation
    // does, and the delivery screen also ignores any submit that is not its
    // own (utils/formSubmit.isOwnSubmit).
    return ReactDOM.createPortal(
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="inline-crm-creator-title"
            style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(15, 23, 42, 0.55)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10000,
                padding: '1rem',
            }}
            onClick={(e) => {
                if (e.target === e.currentTarget && !isSubmitting) onClose?.();
            }}
        >
            <div
                style={{
                    backgroundColor: 'white',
                    borderRadius: '0.75rem',
                    boxShadow: '0 20px 40px rgba(15, 23, 42, 0.25)',
                    width: '100%',
                    maxWidth: '520px',
                    maxHeight: '90vh',
                    overflowY: 'auto',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '1rem 1.25rem',
                        borderBottom: '1px solid #e2e8f0',
                    }}
                >
                    <h2
                        id="inline-crm-creator-title"
                        style={{ margin: 0, fontSize: '1.05rem', fontWeight: 600, color: '#0f172a' }}
                    >
                        <FontAwesomeIcon
                            icon={faUserPlus}
                            style={{ marginRight: '0.5rem', color: '#4a6b25' }}
                        />
                        {title}
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isSubmitting}
                        aria-label="Close"
                        style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#64748b',
                            cursor: isSubmitting ? 'not-allowed' : 'pointer',
                            fontSize: '1rem',
                            padding: '0.25rem 0.5rem',
                        }}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div style={{ padding: '1.25rem' }}>
                    <div
                        style={{
                            backgroundColor: '#fffbeb',
                            border: '1px solid #f59e0b',
                            borderRadius: '0.5rem',
                            padding: '0.75rem 1rem',
                            marginBottom: '1.25rem',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '0.75rem',
                        }}
                    >
                        <FontAwesomeIcon
                            icon={faExclamationTriangle}
                            style={{ color: '#d97706', marginTop: '2px' }}
                        />
                        <div style={{ fontSize: '0.85rem', color: '#78350f', lineHeight: 1.45 }}>
                            <strong>Pending approval.</strong>{' '}
                            New partners created here are submitted for CRM approval
                            and may be referenced on this draft. Final contract sign-off,
                            invoice issuance, and delivery confirmation will require
                            the partner to be approved.
                        </div>
                    </div>

                    {error && (
                        <div
                            role="alert"
                            style={{
                                backgroundColor: '#fef2f2',
                                border: '1px solid #fecaca',
                                borderRadius: '0.5rem',
                                padding: '0.65rem 0.85rem',
                                marginBottom: '1rem',
                                color: '#991b1b',
                                fontSize: '0.85rem',
                            }}
                        >
                            {error}
                        </div>
                    )}

                    <form className="hms-stacked-form" onSubmit={handleSubmit}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
                            <div>
                                <label
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.4rem',
                                        marginBottom: '0.35rem',
                                        fontWeight: 600,
                                        fontSize: '0.85rem',
                                        color: '#374151',
                                    }}
                                >
                                    <FontAwesomeIcon icon={faBuilding} /> Legal Entity Name
                                    <span style={{ color: '#dc2626' }}>*</span>
                                </label>
                                <input
                                    type="text"
                                    value={legalEntityName}
                                    onChange={(e) => setLegalEntityName(e.target.value)}
                                    placeholder="Official legal name of the company"
                                    autoFocus
                                    required
                                    disabled={isSubmitting}
                                    style={{
                                        padding: '0.6rem 0.75rem',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '0.375rem',
                                        fontSize: '0.875rem',
                                        width: '100%',
                                        boxSizing: 'border-box',
                                    }}
                                />
                            </div>

                            <div>
                                <label
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.4rem',
                                        marginBottom: '0.35rem',
                                        fontWeight: 600,
                                        fontSize: '0.85rem',
                                        color: '#374151',
                                    }}
                                >
                                    <FontAwesomeIcon icon={faTag} /> Trading Short Name
                                </label>
                                <input
                                    type="text"
                                    value={tradingShortName}
                                    onChange={(e) => setTradingShortName(e.target.value)}
                                    placeholder="Optional — common trading name or abbreviation"
                                    disabled={isSubmitting}
                                    style={{
                                        padding: '0.6rem 0.75rem',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '0.375rem',
                                        fontSize: '0.875rem',
                                        width: '100%',
                                        boxSizing: 'border-box',
                                    }}
                                />
                            </div>

                            <div>
                                <label
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.4rem',
                                        marginBottom: '0.35rem',
                                        fontWeight: 600,
                                        fontSize: '0.85rem',
                                        color: '#374151',
                                    }}
                                >
                                    <FontAwesomeIcon icon={faGlobe} /> Country
                                </label>
                                <select
                                    value={country}
                                    onChange={(e) => setCountry(e.target.value)}
                                    disabled={isSubmitting}
                                    style={{
                                        padding: '0.6rem 0.75rem',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '0.375rem',
                                        fontSize: '0.875rem',
                                        width: '100%',
                                        backgroundColor: 'white',
                                        boxSizing: 'border-box',
                                    }}
                                >
                                    <option value="">Select country (optional)</option>
                                    {COUNTRY_OPTIONS.map((c) => (
                                        <option key={c.value} value={c.value}>
                                            {c.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.4rem',
                                        marginBottom: '0.35rem',
                                        fontWeight: 600,
                                        fontSize: '0.85rem',
                                        color: '#374151',
                                    }}
                                >
                                    <FontAwesomeIcon icon={faUserPlus} /> Partner Type
                                </label>
                                <select
                                    value={partnerType}
                                    onChange={(e) => setPartnerType(e.target.value)}
                                    disabled={isSubmitting}
                                    style={{
                                        padding: '0.6rem 0.75rem',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '0.375rem',
                                        fontSize: '0.875rem',
                                        width: '100%',
                                        backgroundColor: 'white',
                                        boxSizing: 'border-box',
                                    }}
                                >
                                    {PARTNER_TYPE_OPTIONS.map((pt) => (
                                        <option key={pt} value={pt}>
                                            {pt}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                gap: '0.65rem',
                                justifyContent: 'flex-end',
                                marginTop: '1.5rem',
                                paddingTop: '1rem',
                                borderTop: '1px solid #f1f5f9',
                            }}
                        >
                            <button
                                type="button"
                                onClick={onClose}
                                disabled={isSubmitting}
                                style={{
                                    padding: '0.6rem 1rem',
                                    backgroundColor: '#ffffff',
                                    color: '#475569',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '0.375rem',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={!canSubmit}
                                style={{
                                    padding: '0.6rem 1.1rem',
                                    background: canSubmit
                                        ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
                                        : '#94a3b8',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '0.375rem',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    cursor: canSubmit ? 'pointer' : 'not-allowed',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    minWidth: '160px',
                                    justifyContent: 'center',
                                    boxShadow: canSubmit ? '0 4px 12px rgba(59, 130, 246, 0.4)' : 'none',
                                }}
                            >
                                {isSubmitting ? (
                                    <>
                                        <FontAwesomeIcon icon={faSpinner} spin /> Creating...
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faUserPlus} /> Create Partner
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default InlineCrmCreator;
